import React, { FC, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { fetchData } from '../../helpers/apiUtils';
import { DnvAssetAnalysisSend } from '../../helpers/dnvAssetAnalysisData';

const statusChip = (s: DnvAssetAnalysisSend['status']) => {
  if (s === 'confirmed') return <Chip label="Versendet" color="success" size="small" />;
  if (s === 'cancelled') return <Chip label="Verworfen" color="error" size="small" />;
  return <Chip label="Reserviert" color="warning" size="small" />;
};

const DnvSendsHistory: FC = () => {
  const [rows, setRows] = useState<DnvAssetAnalysisSend[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      const result = await fetchData<DnvAssetAnalysisSend[]>(
        '/api/dnv-asset-analysis/sends?limit=200',
      );
      if (cancelled) return;
      if (result.success && result.data) {
        setRows(result.data);
      } else {
        setError(result.error || 'Fehler beim Laden des Verlaufs');
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Box>
      <Typography variant="h6" mb={2}>
        Verlauf
      </Typography>
      <Typography variant="body2" color="textSecondary" mb={2}>
        Letzte 200 Renderings (alle Stati). Lead-Attribution erfolgt via Adref-Suffix{' '}
        <code>:DNV-Send-ID-&lt;id&gt;</code>.
      </Typography>
      <TableContainer
        component={Paper}
        sx={{ backgroundColor: '#ffffff', maxHeight: 700 }}
      >
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {['ID', 'Send-Datum', 'Asset', 'Mailing-ID', 'SAM', 'Subject', 'Status', 'Erstellt'].map(
                (h) => (
                  <TableCell
                    key={h}
                    sx={{ backgroundColor: '#e0e0e0 !important', color: 'black', fontWeight: 'bold' }}
                  >
                    {h}
                  </TableCell>
                ),
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  Noch keine Sends vorhanden.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell>{r.id}</TableCell>
                  <TableCell>{r.send_date}</TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2">{r.asset_name}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        {r.asset_isin}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{r.mailing_id}</TableCell>
                  <TableCell>{r.sam_id}</TableCell>
                  <TableCell sx={{ maxWidth: 320 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {r.subject || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>{statusChip(r.status)}</TableCell>
                  <TableCell sx={{ fontSize: 12 }}>{r.created_at}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default DnvSendsHistory;
